import { useState } from 'react';
import { useLocale } from 'next-intl';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useRouter, usePathname } from 'src/i18n/navigation';

import { Iconify } from 'src/components/iconify';

const locales = [
  {
    code: 'sv',
    name: 'Svenska',
    flag: 'emojione:flag-for-sweden',
  },
  {
    code: 'en',
    name: 'English',
    flag: 'emojione:flag-for-united-kingdom',
  },
] as const;

export function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLocale = locales.find((l) => l.code === locale);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLocale: 'sv' | 'en') => {
    router.replace(pathname, { locale: newLocale });
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="Change language"
      >
        <Iconify icon={currentLocale?.flag || 'emojione:flag-for-sweden'} width={24} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="language-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {locales.map((localeOption) => (
          <MenuItem
            key={localeOption.code}
            onClick={() => handleLanguageChange(localeOption.code)}
            selected={locale === localeOption.code}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Iconify icon={localeOption.flag} width={20} />
              <Typography variant="body2">{localeOption.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
